import React from 'react';
import { MODE_NAMES, arpeggioTypesFor } from '../../lib/theory';
import {
  FIDDLE_POSITION_OPTIONS,
  FIDDLE_DOUBLE_STOP_OPTIONS,
} from '../../lib/fiddle-theory';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import {
  Tray, ControlCard, TrayLabel, KeyRows, StringsCard, StandardQuickButtons,
} from '../shared';

// ── Data ──────────────────────────────────────────────────────────────────────
// Only display labels live here. The option VALUES are the state values App.js and the
// SEO deep links use ('Full Scale', 'Pentatonic', 'Double Stops', 'Modes', 'Arpeggios', ...).

/** Five structures, so the phone tray uses short labels (rebuild plan, open question 3). */
const STRUCTURE_OPTIONS_PHONE = [
  { value: 'Full Scale',   label: 'Scale' },
  { value: 'Pentatonic',   label: 'Penta' },
  { value: 'Double Stops', label: 'Doubles' },
  { value: 'Modes',        label: 'Modes' },
  { value: 'Arpeggios',    label: 'Arps' },
];
/** Desktop: KeyRows lays 5 options out 3 + 2 with a wider second row, so the long labels go last. */
const STRUCTURE_OPTIONS_DESKTOP = [
  { value: 'Full Scale',   label: 'Scale' },
  { value: 'Pentatonic',   label: 'Pentatonic' },
  { value: 'Modes',        label: 'Modes' },
  { value: 'Double Stops', label: 'Double Stops' },
  { value: 'Arpeggios',    label: 'Arpeggios' },
];

/** Strings low to high, matching fiddleStringFocus (G D A E). */
const STRINGS = [
  { label: 'G', ariaLabel: 'G string' },
  { label: 'D', ariaLabel: 'D string' },
  { label: 'A', ariaLabel: 'A string' },
  { label: 'E', ariaLabel: 'E string' },
];

/** Fretless-only overlays; toggled independently. (The millimetre Ruler was removed in Oct 2026.) */
export const FIDDLE_GUIDE_OPTIONS = ['Tapes', 'Half Steps'];

/** Labels quick button cycle: fiddlers think in fingers, so Fingering joins Names and Intervals. */
export const FIDDLE_LABEL_CYCLE = ['Note Names', 'Intervals', 'Fingering'];

// ── Controls: Key card + Strings card + Fingerboard card ──────────────────────

/**
 * Fiddle controls (boards 18/20 pattern): the Key card (with the Interval, Mode or
 * arpeggio follow-up tray under Structure), the Strings card, and a Fingerboard card
 * holding Position and Guides. The quick buttons live in {@link FiddleQuickButtons}.
 */
export default function FiddleControls({
  fiddleKey,         onFiddleKeyChange,
  fiddleTonality,    onFiddleTonalityChange,
  fiddleStructure,   onFiddleStructureChange,
  fiddleMode,        onFiddleModeChange,
  fiddleShape,       onFiddleShapeChange,
  fiddlePosition,    onFiddlePositionChange,
  fiddleStringFocus, onFiddleStringToggle,
  fiddleGuides,      onFiddleGuideToggle,
  fiddleArpeggioType, onFiddleArpeggioTypeChange,
}) {
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 'md' : 'sm';
  const isDoubleStops = fiddleStructure === 'Double Stops';
  const isModes = fiddleStructure === 'Modes';
  const isArpeggios = fiddleStructure === 'Arpeggios';
  const { basic, extensions } = arpeggioTypesFor(fiddleTonality);

  return (
    <>
      <ControlCard title="Key" size={size}>
        <KeyRows
          isDesktop={isDesktop}
          keyValue={fiddleKey}
          onKeyChange={onFiddleKeyChange}
          tonality={fiddleTonality}
          onTonalityChange={onFiddleTonalityChange}
          // A mode sets its own quality, so Major/Minor sits out while Modes is on.
          tonalityDisabled={isModes}
          structureOptions={STRUCTURE_OPTIONS_PHONE}
          structureOptionsDesktop={STRUCTURE_OPTIONS_DESKTOP}
          structure={fiddleStructure}
          onStructureChange={onFiddleStructureChange}
        />

        {isDoubleStops && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Interval</TrayLabel>}
            <Tray
              ariaLabel="Double stop interval"
              options={FIDDLE_DOUBLE_STOP_OPTIONS}
              value={fiddleShape}
              onChange={onFiddleShapeChange}
              columns={FIDDLE_DOUBLE_STOP_OPTIONS.length}
            />
          </>
        )}

        {isModes && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Mode</TrayLabel>}
            <Tray
              ariaLabel="Mode"
              options={MODE_NAMES}
              value={fiddleMode}
              onChange={onFiddleModeChange}
              columns={isDesktop ? 3 : 4}
            />
          </>
        )}

        {isArpeggios && (
          <>
            {isDesktop && <TrayLabel className="mt-1">Basic</TrayLabel>}
            <Tray
              ariaLabel="Basic arpeggio type"
              options={basic}
              value={fiddleArpeggioType}
              onChange={onFiddleArpeggioTypeChange}
              columns={5}
            />
            {isDesktop && <TrayLabel>Extended</TrayLabel>}
            <Tray
              ariaLabel="Extended arpeggio type"
              options={extensions}
              value={fiddleArpeggioType}
              onChange={onFiddleArpeggioTypeChange}
              {...(isDesktop ? { columns: 5 } : { scroll: true })}
            />
          </>
        )}
      </ControlCard>

      <StringsCard strings={STRINGS} focus={fiddleStringFocus} onToggle={onFiddleStringToggle} size={size} />

      <ControlCard title="Fingerboard" size={size}>
        <TrayLabel>Position</TrayLabel>
        <Tray
          ariaLabel="Position"
          options={FIDDLE_POSITION_OPTIONS}
          value={fiddlePosition}
          onChange={onFiddlePositionChange}
          columns={FIDDLE_POSITION_OPTIONS.length}
        />
        <TrayLabel className="mt-1">Guides</TrayLabel>
        <Tray
          multiple
          ariaLabel="Guides"
          options={FIDDLE_GUIDE_OPTIONS}
          value={fiddleGuides}
          onChange={onFiddleGuideToggle}
          columns={FIDDLE_GUIDE_OPTIONS.length}
        />
      </ControlCard>
    </>
  );
}

// ── Quick buttons: Filter / Labels / Reset ────────────────────────────────────

/**
 * The three quick buttons for the layout's `quickButtons` slot.
 * Filter cycles All Notes -> Triads Only -> Roots Only and is greyed for Arpeggios (every chord tone
 * always shows). Labels cycles Names -> Intervals -> Fingering ('None' counts as Names).
 */
export function FiddleQuickButtons({
  fiddleStructure,
  fiddleFilter, onFiddleFilterChange,
  fiddleLabel,  onFiddleLabelChange,
  onFiddleReset,
}) {
  const isDesktop = useIsDesktop();
  return (
    <StandardQuickButtons
      size={isDesktop ? 'md' : 'sm'}
      filter={fiddleFilter}
      onFilterChange={onFiddleFilterChange}
      filterDisabled={fiddleStructure === 'Arpeggios'}
      label={fiddleLabel}
      onLabelChange={onFiddleLabelChange}
      labelCycle={FIDDLE_LABEL_CYCLE}
      onReset={onFiddleReset}
    />
  );
}
